import React from "react";

// Deterministic gradient palette for celebrity initials avatars.
// Stable per-name so the same person always gets the same colors.
const PALETTES = [
  ["#FFB800", "#F97316"], ["#8B5CF6", "#EC4899"], ["#3B82F6", "#06B6D4"],
  ["#10B981", "#14B8A6"], ["#F43F5E", "#F59E0B"], ["#A855F7", "#6366F1"],
  ["#0EA5E9", "#8B5CF6"], ["#EF4444", "#D97706"], ["#22C55E", "#15803D"],
  ["#EC4899", "#F43F5E"], ["#1E40AF", "#7C3AED"], ["#B45309", "#92400E"],
];

function hashCode(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i), h |= 0;
  return Math.abs(h);
}

function initials(name) {
  // Extract letters only — strip parentheses and other non-alpha tokens.
  const cleaned = name.replace(/[^A-Za-z\s]/g, " ");
  return cleaned
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
}

/**
 * CelebrityAvatar — deterministic gradient + initials.
 * Use when external photos are unreliable.
 *
 * Props:
 *  - size: number (px) or string (e.g. "100%"). When string, pass fontSize separately.
 *  - fontSize: optional override (number, px). Auto-derived from numeric size.
 */
export default function CelebrityAvatar({ name, className = "", size = 64, fontSize }) {
  const [c1, c2] = PALETTES[hashCode(name) % PALETTES.length];
  const ini = initials(name);
  const isNumeric = typeof size === "number";
  const fs = fontSize ?? (isNumeric ? Math.round(size * 0.38) : 64);
  return (
    <div
      className={`flex items-center justify-center font-serif-display select-none ${className}`}
      style={{
        background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`,
        color: "rgba(255,255,255,0.96)",
        width: isNumeric ? `${size}px` : size,
        height: isNumeric ? `${size}px` : size,
        fontSize: `${fs}px`,
        letterSpacing: "-0.02em",
        textShadow: "0 2px 6px rgba(0,0,0,0.3)",
      }}
      aria-label={name}
      data-testid="celebrity-avatar"
    >
      {ini}
    </div>
  );
}
